import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/** How long a deep link waits for its section to draw the target before giving up. */
export const SETTINGS_HASH_WAIT_MS = 10_000;

/** What the user does to read the page their own way, which a late jump would undo. */
const USER_SCROLL_EVENTS = ["wheel", "touchstart", "keydown", "pointerdown"] as const;

/**
 * Scroll to a settings deep-link's hash target (e.g. /settings/kids-checkin#bg-check-vendor).
 * React Router does not auto-scroll to hash fragments, and the section content mounts after
 * navigation, so a one-frame defer lets the target element render before we scroll to it. A
 * section can draw later than that (its skeleton shows while the viewer's permissions or its data
 * load, with no change to the address), so until the target appears the hook keeps watching the
 * page, then scrolls once. It stops when the address changes, when the user starts moving around
 * the page themselves, or after SETTINGS_HASH_WAIT_MS.
 *
 * The classic SettingsLayout runs a one-frame version of this inline and is left untouched while
 * classic Settings remain (#7224 step 4); it can call this hook once the classic variant retires.
 */
export function useScrollToSettingsHash() {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    let frame = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let observer: MutationObserver | null = null;

    const stopWatching = () => {
      observer?.disconnect();
      observer = null;
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
      for (const type of USER_SCROLL_EVENTS) window.removeEventListener(type, stop);
    };
    function stop() {
      cancelAnimationFrame(frame);
      stopWatching();
    }
    // Scroll a frame after the target is in the page, so what draws with it has laid out too.
    const scrollWhenDrawn = () => {
      if (!document.getElementById(id)) return false;
      stopWatching();
      frame = requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      return true;
    };

    frame = requestAnimationFrame(() => {
      if (scrollWhenDrawn() || typeof MutationObserver === "undefined") return;
      observer = new MutationObserver(() => { scrollWhenDrawn(); });
      observer.observe(document.body, { childList: true, subtree: true });
      timer = setTimeout(stop, SETTINGS_HASH_WAIT_MS);
      for (const type of USER_SCROLL_EVENTS) window.addEventListener(type, stop, { passive: true });
    });
    return stop;
  }, [location.pathname, location.hash]);
}
